import { InternalConjunctionalAgencySchema } from '@buyerflo/core/schemas/conjunctionalAgency'
import { ChevronRight, Plus } from '@tamagui/lucide-icons-2'
import { useConjunctionalAgenciesApi, useUsersApi } from 'apiProviders'
import { useUserData } from 'auth'
import Button from 'components/button'
import Loading from 'components/Loading'
import { sortBy } from 'ramda'
import { useState } from 'react'
import { SizableText, Text, View, XStack, YStack } from 'tamagui'
import { AgencyDrawer } from './agencyDrawer'

const HeaderCell = ({ children, flex }: { children?: React.ReactNode; flex: number }) => (
    <View
        flex={flex}
        paddingHorizontal="$2"
    >
        <Text
            fontWeight="600"
            fontSize="$2"
            color="$colorMuted"
            textTransform="uppercase"
            letterSpacing={0.5}
        >
            {children}
        </Text>
    </View>
)

const initials = (name: string) =>
    name
        .split(' ')
        .map(w => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()

export default function AgenciesPanel() {
    const conjunctionalAgenciesApi = useConjunctionalAgenciesApi()
    const usersApi = useUsersApi()
    const { user } = useUserData()
    const [editAgency, setEditAgency] = useState<{} | InternalConjunctionalAgencySchema | undefined>()

    const canEdit = user.permissions.includes('users.edit')

    if (!conjunctionalAgenciesApi.data || !usersApi.data) {
        return <Loading />
    }

    const agencies = sortBy(
        (a: InternalConjunctionalAgencySchema) => a.licenseeName.toLowerCase(),
        conjunctionalAgenciesApi.data as InternalConjunctionalAgencySchema[],
    )
    const agentCount = (agencyId: string) =>
        (usersApi.data ?? []).filter(u => u.conjunctionalAgencyId === agencyId).length

    return (
        <YStack gap="$3">
            <XStack
                alignItems="center"
                justifyContent="space-between"
                gap="$3"
            >
                <SizableText
                    size="$2"
                    color="$colorMuted"
                >
                    {agencies.length} external{' '}
                    {agencies.length === 1 ? 'agency you work' : 'agencies you work'} with on shared listings
                </SizableText>
                {canEdit && (
                    <Button
                        theme="primary"
                        fontWeight="600"
                        borderRadius="$4"
                        icon={<Plus size={16} />}
                        onPress={() => setEditAgency({})}
                        data-help-id="agencies-new-button"
                    >
                        Add Agency
                    </Button>
                )}
            </XStack>

            <YStack
                backgroundColor="$backgroundStrong"
                borderWidth={1}
                borderColor="$borderColor"
                borderRadius={14}
                overflow="hidden"
                data-help-id="agencies-list"
            >
                <XStack
                    backgroundColor="$background"
                    borderBottomWidth={1}
                    borderBottomColor="$borderColor"
                    paddingVertical="$3"
                    paddingHorizontal="$4"
                >
                    <HeaderCell flex={1.8}>Agency</HeaderCell>
                    <HeaderCell flex={1}>Licence #</HeaderCell>
                    <HeaderCell flex={0.8}>Commission</HeaderCell>
                    <HeaderCell flex={0.6}>Agents</HeaderCell>
                    <View width={28} />
                </XStack>

                {agencies.map((agency, index) => (
                    <XStack
                        key={agency.conjunctionalAgencyId}
                        paddingVertical="$3"
                        paddingHorizontal="$4"
                        alignItems="center"
                        cursor="pointer"
                        animation="quick"
                        hoverStyle={{ backgroundColor: '$backgroundHover' }}
                        pressStyle={{ backgroundColor: '$backgroundPress' }}
                        borderBottomWidth={index < agencies.length - 1 ? 1 : 0}
                        borderBottomColor="$borderColor"
                        onPress={() => setEditAgency(agency)}
                    >
                        <XStack
                            flex={1.8}
                            paddingHorizontal="$2"
                            gap="$3"
                            alignItems="center"
                            minWidth={0}
                        >
                            <View
                                width={32}
                                height={32}
                                borderRadius="$3"
                                backgroundColor="$backgroundRaised"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <SizableText
                                    size="$1"
                                    fontWeight="700"
                                    color="$colorSoft"
                                >
                                    {initials(agency.licenseeName)}
                                </SizableText>
                            </View>
                            <Text
                                fontSize="$3"
                                fontWeight="600"
                                numberOfLines={1}
                            >
                                {agency.licenseeName}
                            </Text>
                        </XStack>
                        <View
                            flex={1}
                            paddingHorizontal="$2"
                        >
                            <Text
                                fontSize="$2"
                                fontFamily="$mono"
                                color="$colorSoft"
                                numberOfLines={1}
                            >
                                {agency.licenseNumber || '—'}
                            </Text>
                        </View>
                        <View
                            flex={0.8}
                            paddingHorizontal="$2"
                        >
                            <Text
                                fontSize="$2"
                                fontFamily="$mono"
                                color="$colorSoft"
                            >
                                {agency.agencyCommission ? `${agency.agencyCommission}%` : '—'}
                            </Text>
                        </View>
                        <View
                            flex={0.6}
                            paddingHorizontal="$2"
                        >
                            <Text
                                fontSize="$2"
                                fontFamily="$mono"
                                color="$colorSoft"
                            >
                                {agentCount(agency.conjunctionalAgencyId) || '—'}
                            </Text>
                        </View>
                        <View
                            width={28}
                            alignItems="center"
                        >
                            <ChevronRight
                                size={16}
                                color="$colorFaint"
                            />
                        </View>
                    </XStack>
                ))}

                {agencies.length === 0 && (
                    <View
                        padding="$8"
                        alignItems="center"
                    >
                        <SizableText color="$colorSoft">No conjunctional agencies yet.</SizableText>
                    </View>
                )}
            </YStack>

            {editAgency !== undefined && (
                <AgencyDrawer
                    key={(editAgency as InternalConjunctionalAgencySchema).conjunctionalAgencyId ?? 'new'}
                    agency={editAgency}
                    users={usersApi.data ?? []}
                    onClose={() => setEditAgency(undefined)}
                />
            )}
        </YStack>
    )
}
